import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { redirect, useLoaderData, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Header } from "../layout/header.js";
import { Footer } from "../layout/footer.js";
import { ApiNavRail } from "../components/api/ApiNavRail.js";
import { ApiJumpSelect } from "../components/api/ApiJumpSelect.js";
import { ApiGrammarCard } from "../components/api/ApiGrammarCard.js";
import { ApiResourceSection } from "../components/api/ApiResourceSection.js";
import { ApiErrorsSection } from "../components/api/ApiErrorsSection.js";
import { ApiTypesSection } from "../components/api/ApiTypesSection.js";
import { ApiResponsePanel } from "../components/api/ApiResponsePanel.js";
import { copyText } from "../components/api/clipboard.js";
import { buildApiModel, resourceFromPath,hasPathParams, fillPathParams, sampleIdFrom } from "./apipage_utils.js";

// The root URL starts at the top of the page, so the first resource in the rail is the
// one highlighted and loaded; anything else would contradict the scroll position.
const DEFAULT_RESOURCE = "observatories";
// Where the reference page lives. Everything else under /api/ is the API itself, served by Django.
const DOCS_PATH = "/api/doc";
const THREE_COLUMN_MIN = 1000; // $bp-xl
const TWO_COLUMN_MIN = 768; // $bp-lg

export async function loader({ params }) {
  let schema;
  try {
    const response = await axios.get("/api/schema/?format=json");
    schema = response.data;
  } catch (error) {
    return redirect("/error");
  }
  const model = buildApiModel(schema);
  const allResources = model.groups.flatMap((g) => g.resources);
  const requested = resourceFromPath(params["*"]);
  const activeResourceId = requested === "" ? DEFAULT_RESOURCE : requested;
  if (!allResources.some((r) => r.id === activeResourceId)) {
    return redirect("/error");
  }
  return { model, activeResourceId, deepLinked: requested !== "" };
}

function layoutFor(width) {
  if (width >= THREE_COLUMN_MIN) return "three";
  if (width >= TWO_COLUMN_MIN) return "two";
  return "one";
}

function useLayoutMode() {
  const [mode, setMode] = useState(() => layoutFor(window.innerWidth));
  useEffect(() => {
    const onResize = () => setMode(layoutFor(window.innerWidth));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mode;
}

/** Scrolls smoothly unless the user asked the OS for reduced motion (jsdom has no matchMedia). */
function scrollToElement(el) {
  if (!el) return;
  const reduceMotion = Boolean(
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.75"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="7" y="7" width="10" height="10" /><path d="M4 13V4h9" />
    </svg>
  );
}

export function ApiPageContent() {
  const { model, activeResourceId, deepLinked } = useLoaderData();
  const navigate = useNavigate();
  const location = useLocation();
  const layout = useLayoutMode();
  const resources = useMemo(() => model.groups.flatMap((g) => g.resources), [model]);

  const initialResource = resources.find((r) => r.id === activeResourceId);
  const initialOp = initialResource.listOp || initialResource.operations[0];
  const [activeOp, setActiveOp] = useState(initialOp);
  const [activeResource, setActiveResource] = useState(activeResourceId);
  // The resource whose section holds the live panel (inline layouts) — changes only on
  // an explicit action (op click, nav choice, submitted request edit, external nav).
  // Never touched by the scrollspy, so panel placement and its request never move on scroll.
  const [panelResource, setPanelResource] = useState(activeResourceId);
  const [requestPath, setRequestPath] = useState(initialOp.path);
  const [basePathCopied, setBasePathCopied] = useState(false);
  const sectionRefs = useRef({});
  const activeResourceRef = useRef(activeResource);
  const panelResourceRef = useRef(panelResource);

  const sampleIdsRef = useRef({});    // resourceId -> Promise<id | null>
  const pendingOpRef = useRef(null);  // op id whose sample-id lookup is still in flight

  const sampleIdFor = useCallback((resource) => {
    if (!resource.listOp) return Promise.resolve(null);
    if (!sampleIdsRef.current[resource.id]) {
      sampleIdsRef.current[resource.id] = axios
        .get(resource.listOp.path)
        .then((res) => sampleIdFrom(res.data))
        .catch(() => {
          delete sampleIdsRef.current[resource.id]; // allow a retry on the next click
          return null;
        });
    }
    return sampleIdsRef.current[resource.id];
  }, []);

  // Mirror the latest state into refs after each commit (never during render) so the
  // URL-sync effect below can read the current panelResource without depending on it.
  useEffect(() => {
    activeResourceRef.current = activeResource;
    panelResourceRef.current = panelResource;
  });

  const resourceOfOp = useCallback(
    (op) => resources.find((r) => r.operations.some((o) => o.id === op.id)),
    [resources]
  );

  // Scrollspy: the section nearest the top of the viewport becomes the highlighted nav
  // entry only. It never moves the live panel or re-runs its request.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length === 0) return;
        setActiveResource(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );
    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [resources]);

  // Scroll to the deep-linked section once, on mount. "/api/doc/" (not deep-linked) stays at the top.
  useEffect(() => {
    if (!deepLinked) return;
    scrollToElement(sectionRefs.current[activeResourceId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep state in sync with the URL after navigation the page didn't itself trigger
  // (browser Back/Forward between visited resources, an address-bar edit). Depends
  // only on the pathname (never on panelResource, read via the ref instead) so
  // scrollspy — which changes activeResource without touching the URL — never
  // re-triggers this effect and fights it. selectOp and onRequestPathChange already
  // update panelResource before they navigate, so this is a no-op for those.
  useEffect(() => {
    const id = resourceFromPath(location.pathname.replace(/^\/api\/doc\/?/, "")) || DEFAULT_RESOURCE;
    if (!resources.some((r) => r.id === id)) return;
    if (id === panelResourceRef.current) return;
    const r = resources.find((x) => x.id === id);
    const op = r.listOp || r.operations[0];
    setActiveResource(id);
    setPanelResource(id);
    setActiveOp(op);
    setRequestPath(op.path);
    scrollToElement(sectionRefs.current[id]);
  }, [location.pathname, resources]);

  const selectOp = (op) => {
    const r = resourceOfOp(op);
    setActiveResource(r.id);
    setPanelResource(r.id);
    setActiveOp(op);
    navigate(`${DOCS_PATH}/${r.id}`, { replace: true });

    if (!hasPathParams(op.path)) {
      pendingOpRef.current = null;
      setRequestPath(op.path);
      return;
    }

    pendingOpRef.current = op.id;
    sampleIdFor(r).then((id) => {
      if (pendingOpRef.current !== op.id) return; // user clicked something else meanwhile
      pendingOpRef.current = null;
      setRequestPath(id != null ? fillPathParams(op.path, id) : op.path);
    });
  };

  const jumpTo = (id) => {
    scrollToElement(document.getElementById(id));
    const r = resources.find((x) => x.id === id);
    if (r) {
      selectOp(r.listOp || r.operations[0]);
    } else if (id === "grammar" || id === "types" || id === "errors") {
      // No matching resource: just let the mobile <select> show the chosen entry
      // instead of snapping back. The nav rail highlights nothing for these ids.
      setActiveResource(id);
    }
  };

  const onRequestPathChange = (path) => {
    pendingOpRef.current = null;
    setRequestPath(path);
    // Keep the URL (and the panel's home section) on the resource the path names, when it names one.
    const id = resourceFromPath(path.replace(/^\/api\//, "").split("?")[0]);
    if (resources.some((r) => r.id === id)) {
      setActiveResource(id);
      setPanelResource(id);
      navigate(`${DOCS_PATH}/${id}`, { replace: true });
    }
  };

  const onPanelEscape = useCallback(() => {
    if (!activeOp) return;
    const el = document.querySelector(`[data-op-id="${activeOp.id}"]`);
    if (el) el.focus();
  }, [activeOp]);

  const copyBasePath = async () => {
    const ok = await copyText(`${window.location.origin}/api/`);
    if (ok) {
      setBasePathCopied(true);
      setTimeout(() => setBasePathCopied(false), 1500);
    }
  };

  const panel = (
    <ApiResponsePanel requestPath={requestPath} onRequestPathChange={onRequestPathChange} onEscape={onPanelEscape} />
  );
  const panelInline = layout !== "three";

  return (
    <div className="apiPage">
      <Header scrollToTop={false} />

      <section className="apiTitleBand">
        <div className="apiTitleBandInner">
          <div className="apiTitleText">
            <span className="apiEyebrow">Developer reference</span>
            <h1 className="apiTitle">HelioData Discovery API</h1>
            <p className="apiSubtitle">
              Programmatic, read-only access to the HelioData catalog: observatories, instruments, datasets,
              and the vocabularies that describe them.
            </p>
            <div className="apiMetaRow">
              <span className="apiMeta">Version {model.version}</span><span className="apiMetaDot" aria-hidden="true" />
              <span className="apiMeta">Read-only, all GET</span><span className="apiMetaDot" aria-hidden="true" />
              <span className="apiMeta">No API key required</span>
            </div>
          </div>
          <div className="apiTitleAside">
            <span className="apiMeta">Base path</span>
            <span className="apiBasePath">
              {model.basePath}
              <button type="button" className="apiIconButton" onClick={copyBasePath} aria-label="Copy base URL">
                <CopyIcon />
                <span className="apiSrOnly" aria-live="polite">{basePathCopied ? "Copied" : ""}</span>
              </button>
            </span>
            <a className="apiTextLink" href="/api/schema/?format=json" target="_blank" rel="noreferrer">OpenAPI 3 schema</a>
          </div>
        </div>
      </section>

      <div className={`apiWorkspace apiWorkspace--${layout}`}>
        {layout === "one" ? (
          <ApiJumpSelect groups={model.groups} activeId={activeResource} onSelect={jumpTo} />
        ) : (
          <div className="apiSticky">
            <ApiNavRail groups={model.groups} activeId={activeResource} onSelect={jumpTo} />
          </div>
        )}

        <div className="apiDocs">
          <ApiGrammarCard />
          {model.groups.map((group) =>
            group.resources.map((resource) => (
              <Fragment key={resource.id}>
                <div className="apiHairline" />
                <ApiResourceSection
                  resource={resource}
                  groupLabel={group.label}
                  activeOpId={activeOp && activeOp.id}
                  onSelectOp={selectOp}
                  typeNames={model.typeNames}
                  sectionRef={(el) => { sectionRefs.current[resource.id] = el; }}
                >
                  {panelInline && panelResource === resource.id ? panel : null}
                </ApiResourceSection>
              </Fragment>
            ))
          )}
          <div className="apiHairline" />
          <ApiTypesSection types={model.types} typeNames={model.typeNames} />
          <div className="apiHairline" />
          <ApiErrorsSection />
        </div>

        {!panelInline && <div className="apiSticky">{panel}</div>}
      </div>

      <Footer />
    </div>
  );
}
